import * as React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { useAuth } from '../../Context/AuthContext'
import { toast } from 'react-toastify'

const MfaModal = ({ open, userId, mfaType, rememberMe, onClose, onSuccess }) => {
  const { verifyEmailMfa, verifyPhoneMfa, resendEmailMfa } = useAuth()
  const [code, setCode] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [resending, setResending] = React.useState(false)
  const [error, setError] = React.useState('')
  const [countdown, setCountdown] = React.useState(0)
  const timerRef = React.useRef(null)

  React.useEffect(() => {
    if (open) {
      setCode('')
      setError('')
      setCountdown(0)
    }
    return () => clearInterval(timerRef.current)
  }, [open])

  const startCountdown = (seconds = 60) => {
    clearInterval(timerRef.current)
    setCountdown(seconds)
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleVerify = async () => {
    const trimmed = code.trim()
    if (!/^\d{6}$/.test(trimmed)) {
      setError('Enter the 6-digit verification code.')
      return
    }
    setLoading(true)
    setError('')
    try {
      if (mfaType === 'phone') {
        await verifyPhoneMfa(userId, trimmed)
      } else {
        await verifyEmailMfa(userId, trimmed, rememberMe)
      }
      onSuccess()
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    setResending(true)
    setError('')
    try {
      await resendEmailMfa(userId)
      toast.success('Verification code resent.')
      startCountdown(60)
    } catch (err) {
      setError(err.message || 'Failed to resend code.')
    } finally {
      setResending(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleVerify()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Verification Required</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {mfaType === 'phone'
            ? 'Enter the 6-digit code sent to your phone.'
            : 'Enter the 6-digit code sent to your email.'}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Verification Code"
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={handleKeyDown}
          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          error={!!error}
          helperText={error}
          disabled={loading}
        />
        {mfaType === 'email' && (
          <Box sx={{ mt: 1.5 }}>
            <Button
              size="small"
              onClick={handleResend}
              disabled={resending || countdown > 0}
            >
              {resending
                ? 'Resending…'
                : countdown > 0
                ? `Resend in ${countdown}s`
                : 'Resend Code'}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleVerify}
          disabled={loading || code.length !== 6}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 'Verifying…' : 'Verify'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MfaModal
